import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, X, AlertTriangle, TrendingUp, Scale, Shield } from "lucide-react";
import { api } from "@/lib/api";

/**
 * SignalsInbox — banner surface for proactive Buy/Sell signals produced by
 * the backend `signals_engine.py` scanner. The cron runs every 5 minutes;
 * this component also exposes a manual "Scan now" button for on-demand.
 */
const PRIORITY_STYLES = {
    critical: "border-destructive/50 bg-destructive/5 text-destructive",
    high:     "border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400",
    medium:   "border-primary/40 bg-primary/5 text-primary",
    low:      "border-border bg-muted/30 text-foreground",
};

const TYPE_ICON = {
    buy:       TrendingUp,
    sell:      TrendingUp,
    rebalance: Scale,
    risk:      Shield,
};

export default function SignalsInbox({ onClose, onScanned }) {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/chat/assistant/signals");
            setRows(data.signals || []);
            onScanned?.((data.signals || []).length);
        } catch {
            toast.error("Failed to load signals.");
        } finally {
            setLoading(false);
        }
    }

    async function scanNow() {
        setBusy(true);
        try {
            const { data } = await api.post("/chat/assistant/signals/scan");
            toast.success(`${data.count || 0} fresh signal${data.count === 1 ? "" : "s"} generated.`);
            await load();
        } catch {
            toast.error("Scan failed.");
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div
            className="mb-3 border border-border rounded-lg bg-card p-3"
            data-testid="assistant-signals-inbox"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">
                    Proactive signals · last 48h
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={scanNow}
                        disabled={busy}
                        data-testid="assistant-signals-scan"
                        className="text-xs px-2 py-1 rounded border border-border hover:border-primary/50 inline-flex items-center gap-1 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Scan now
                    </button>
                    <button
                        onClick={onClose}
                        aria-label="Close signals"
                        data-testid="assistant-signals-close"
                        className="p-1 rounded hover:bg-secondary"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {loading && (
                <div className="text-xs text-muted-foreground py-4 text-center">Loading…</div>
            )}
            {!loading && rows.length === 0 && (
                <div className="text-xs text-muted-foreground py-4 text-center" data-testid="assistant-signals-empty">
                    No active signals. The scanner sweeps every 5 minutes.
                </div>
            )}
            {!loading && rows.length > 0 && (
                <ul className="space-y-2">
                    {rows.map((r) => {
                        const style = PRIORITY_STYLES[r.priority] || PRIORITY_STYLES.medium;
                        const Icon = TYPE_ICON[r.signal_type] || AlertTriangle;
                        return (
                            <li
                                key={r.id || `${r.signal_type}-${r.symbol}-${r.ts}`}
                                data-testid={`assistant-signal-${r.signal_type}`}
                                className={`border rounded-md p-2.5 ${style}`}
                            >
                                <div className="flex items-start gap-2">
                                    <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-sm">{r.headline}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{r.teaser}</div>
                                        <div className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground mt-1">
                                            {r.signal_type} · {r.priority} · {r.symbol || "portfolio"}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
